import { createEntityAdapter, EntityAdapter } from '@ngrx/entity';
import { createReducer, createSelector, on } from '@ngrx/store';
import Debug from 'debug';
import {
    onBlockingRequestEnded,
    onBlockingRequestStarted,
    onResetOverlay,
} from '../actions/request-loading-indicator.actions';
import { IRequestLoadingIndicator } from '../interfaces/IRequestLoadingIndicator.interface';
const debug = Debug('aumico:report:BlockingRequests');

function selectRequestId(requestLoadingIndicator: IRequestLoadingIndicator): string {
    return requestLoadingIndicator.identifier;
}

const adapter: EntityAdapter<IRequestLoadingIndicator> = createEntityAdapter<IRequestLoadingIndicator>({
    selectId: selectRequestId,
});

export const { selectAll, selectEntities, selectIds } = adapter.getSelectors();

const selectActiveRequest = createSelector(selectAll, (requestCounters: IRequestLoadingIndicator[]) => {
    return requestCounters.filter((r) => r.activeRequest > 0);
});

const initialState = adapter.getInitialState({});

export const selectIsBlockingRequestActive = createSelector(selectActiveRequest, (activeRequestCounters) => {
    return activeRequestCounters.length > 0;
});

// NEW: Selector to get all active request identifiers for debugging
export const selectActiveRequestIdentifiers = createSelector(selectActiveRequest, (activeRequestCounters) => {
    return activeRequestCounters.map((counter) => ({
        identifier: counter.identifier,
        activeCount: counter.activeRequest,
        endedCount: counter.endedRequest,
    }));
});

// NEW: Selector to get count of active requests
export const selectActiveRequestCount = createSelector(selectActiveRequest, (activeRequestCounters) => {
    return activeRequestCounters.reduce((total, counter) => total + counter.activeRequest, 0);
});

export const requestLoadingIndicatorReducer = createReducer(
    initialState,
    on(onBlockingRequestStarted, (state, action) => {
        const identifier = action.payload;
        debug(`🟢 Starting request: ${identifier}`); // DEBUG LOG

        if (state && Object.keys(state.entities).length !== 0) {
            const existingRequestCounter = state.entities[identifier];
            if (existingRequestCounter) {
                const updatedCounter = {
                    ...existingRequestCounter,
                    activeRequest: existingRequestCounter.activeRequest + 1,
                };
                debug(
                    `🔄 Updated existing request ${identifier}: active=${updatedCounter.activeRequest}, ended=${updatedCounter.endedRequest}`,
                ); // DEBUG LOG
                return adapter.upsertOne(updatedCounter, { ...state });
            } else {
                const newRequestCounter: IRequestLoadingIndicator = {
                    identifier,
                    activeRequest: 1,
                    endedRequest: 0,
                };
                debug(`🆕 Created new request ${identifier}: active=1, ended=0`); // DEBUG LOG
                return adapter.upsertOne(newRequestCounter, { ...state });
            }
        } else {
            const newRequestCounter: IRequestLoadingIndicator = {
                identifier,
                activeRequest: 1,
                endedRequest: 0,
            };
            debug(`🆕 Created new request ${identifier}: active=1, ended=0`); // DEBUG LOG
            return adapter.upsertOne(newRequestCounter, { ...state });
        }
    }),
    on(onBlockingRequestEnded, (state, action) => {
        const identifier = action.payload;
        debug(`✅ Ending request: ${identifier}`); // DEBUG LOG

        if (state && Object.keys(state.entities).length !== 0) {
            const existingRequestCounter = state.entities[identifier];
            if (existingRequestCounter) {
                const updatedCounter = {
                    ...existingRequestCounter,
                    activeRequest: Math.max(0, existingRequestCounter.activeRequest - 1), // Prevent negative values
                    endedRequest: existingRequestCounter.endedRequest + 1,
                };
                debug(
                    `🔄 Updated existing request ${identifier}: active=${updatedCounter.activeRequest}, ended=${updatedCounter.endedRequest}`,
                ); // DEBUG LOG
                return adapter.upsertOne(updatedCounter, { ...state });
            } else {
                console.warn(`⚠️ Tried to end non-existent request: ${identifier}`); // DEBUG LOG
            }
            return { ...state };
        } else {
            console.warn(`⚠️ Tried to end request ${identifier} but state is empty`); // DEBUG LOG
            return { ...state };
        }
    }),
    on(onResetOverlay, (state) => {
        debug('🔄 Resetting all request counters'); // DEBUG LOG
        return adapter.removeAll(state);
    }),
);
